import type { ColumnDef } from '@tanstack/react-table';
import { TextLink } from '@shared/components';
import { formatIsoDateOnlyShort } from '@/app/utils/formatDate';
import { formatVerbatimMoneyCurrency } from '@shared/utils/formatMoneyDisplay';
import type { SummariesState } from '../../hooks/useFundAccountPostingsLine';
import type { AgreementSummary } from '../../types/fundAccountPostings.types';
import type { PostingDisplayRow } from '../../utils/postingDisplayRows';
import type { PostingsLayout } from '../../utils/postingsLayout';
import { shortId } from '../../utils/shortId';
import { vatRateLabel } from '../../utils/vatRateLabel';
import { MoneyCell, SummaryText } from './PostingCells';
import { UnsplitBadge } from '../DealerFinancials/FundStatementPeriod/PeriodStatementBody/UnsplitBadge';

export interface PostingsTableMeta extends Record<string, unknown> {
    summaries: Map<string, AgreementSummary>;
    summariesState: SummariesState;
    currency: string;
}

type Column = ColumnDef<PostingDisplayRow>;

const figureHeader = (text: string) => () => <span className="ml-auto">{text}</span>;
type Cell = { row: { original: PostingDisplayRow }; table: { options: { meta?: unknown } } };

const metaOf = (table: Cell['table']) => table.options.meta as PostingsTableMeta;

const summaryOf = (cell: Cell): AgreementSummary | null =>
    metaOf(cell.table).summaries.get(cell.row.original.posting.agreementId.toLowerCase()) ?? null;

const summaryColumn = (id: string, header: string, pick: (summary: AgreementSummary) => string | null): Column => ({
    id,
    header,
    cell: (cell) => {
        const summary = summaryOf(cell);
        return (
            <SummaryText
                loading={metaOf(cell.table).summariesState === 'loading'}
                text={summary ? pick(summary) : null}
            />
        );
    },
});

const agreementColumn: Column = {
    id: 'agreement',
    header: 'Agreement',
    cell: (cell) => {
        const { agreementId } = cell.row.original.posting;
        const label = summaryOf(cell)?.agreementRef ?? shortId(agreementId);
        return (
            <span className="whitespace-nowrap" title={agreementId}>
                <TextLink color="primary" to={`/agreements/${agreementId}`} ariaLabel={`Open agreement ${label}`}>
                    {label}
                </TextLink>
            </span>
        );
    },
};

const sharedColumns: Column[] = [
    agreementColumn,
    summaryColumn('registration', 'Registration', (summary) => summary.vrm),
    summaryColumn('customer', 'Customer', (summary) => summary.customerSurname),
    summaryColumn('soldBy', 'Sold by', (summary) => summary.soldBy),
    summaryColumn('saleDate', 'Sale date', (summary) => formatIsoDateOnlyShort(summary.soldAt)),
    {
        id: 'transDate',
        header: 'Trans. date',
        cell: ({ row }) => (
            <span className="whitespace-nowrap">{formatIsoDateOnlyShort(row.original.posting.effectiveDate)}</span>
        ),
    },
];

const vatRateColumn: Column = {
    id: 'vatRate',
    header: 'VAT rate',
    cell: ({ row }) => {
        const { leg } = row.original;
        return <span className="whitespace-nowrap">{leg ? vatRateLabel(leg.vatRatePercent) : <UnsplitBadge />}</span>;
    },
};

const grossColumn: Column = {
    id: 'gross',
    header: figureHeader('Gross'),
    cell: (cell) => <MoneyCell value={cell.row.original.leg?.gross ?? cell.row.original.posting.amount} currency={metaOf(cell.table).currency} />,
};

const vatColumn: Column = {
    id: 'vat',
    header: figureHeader('VAT'),
    cell: (cell) => <MoneyCell value={cell.row.original.leg?.vat ?? null} currency={metaOf(cell.table).currency} />,
};

const splitNetColumn: Column = {
    id: 'net',
    header: figureHeader('Net'),
    cell: (cell) => <MoneyCell value={cell.row.original.leg?.net ?? null} currency={metaOf(cell.table).currency} />,
};

const netOnlyColumn: Column = {
    id: 'net',
    header: figureHeader('Net'),
    cell: (cell) => {
        const { posting } = cell.row.original;
        if (posting.net !== null) return <MoneyCell value={posting.net} currency={metaOf(cell.table).currency} />;
        return (
            <span className="flex flex-col items-end gap-1 whitespace-nowrap">
                <UnsplitBadge />
                <MoneyCell value={null} currency={metaOf(cell.table).currency} />
                <span className="text-xs text-mp-muted">
                    {formatVerbatimMoneyCurrency(posting.amount, metaOf(cell.table).currency)} gross
                </span>
            </span>
        );
    },
};

const invoiceColumn: Column = {
    id: 'invoice',
    header: 'Invoice',
    cell: ({ row }) => {
        const { invoiceNumber, invoiceId } = row.original.posting;
        return invoiceNumber ?? (invoiceId ? shortId(invoiceId) : '');
    },
};

export const POSTINGS_COLUMNS: Record<PostingsLayout, Column[]> = {
    split: [...sharedColumns, vatRateColumn, grossColumn, vatColumn, splitNetColumn],
    net_with_invoice: [...sharedColumns, invoiceColumn, netOnlyColumn],
    net: [...sharedColumns, netOnlyColumn],
};
